import axios, {AxiosResponse, isAxiosError} from 'axios';
import type {IAvatarApiError, IAvatarApiMethods} from '@wholesalechange/videocomponent';
import {IAvatarSessionErrorResponse, IAvatarSessionTokenResponse, IVideoCall} from '../dialogue/interfaces';

export const mintAvatarSessionTokenApi = (botId: number): Promise<AxiosResponse<IAvatarSessionTokenResponse>> => {
  return axios.post(
    `${process.env.REACT_APP_API_URL}/avatar/session/token`,
    {
      botId
    }
  );
};

export const startAvatarSessionApi = (dialogueId: number, botId: number): Promise<AxiosResponse<IVideoCall>> => {
  return axios.post(
    `${process.env.REACT_APP_API_URL}/video/avatar/start`,
    {
      dialogId: dialogueId,
      botId
    }
  );
};

export const reportAvatarSessionReadyApi = (videoCallId: number, providerSessionId: string): Promise<AxiosResponse<{ value: boolean }>> => {
  return axios.post(
    `${process.env.REACT_APP_API_URL}/video/avatar/session-ready`,
    {
      videoCallId,
      providerSessionId
    }
  );
};

// Uses fetch with `keepalive` rather than axios so the request survives page hide and unload.
export const endAvatarSessionApi = (videoCallId: number): Promise<Response> => {
  const {Authorization, API_KEY: apiKey, CLEVER_TOKEN: cleverToken} = axios.defaults.headers.common;
  const headers: Record<string, string> = {'Content-Type': 'application/json'};

  if (Authorization) headers.Authorization = String(Authorization);
  if (apiKey) headers.API_KEY = String(apiKey);
  if (cleverToken) headers.CLEVER_TOKEN = String(cleverToken);

  return fetch(`${process.env.REACT_APP_API_URL}/video/end`, {
    method: 'POST',
    headers,
    body: JSON.stringify({id: videoCallId}),
    keepalive: true
  });
};

export const toAvatarApiError = (error: unknown): IAvatarApiError => {
  if (isAxiosError<IAvatarSessionErrorResponse>(error)) {
    return {
      status: error.response?.status,
      message: error.response?.data?.error || error.message
    };
  }

  return {message: error instanceof Error ? error.message : String(error)};
};

// Adapts the API calls to the videocomponent contract: resolve with the bare value, reject with IAvatarApiError.
export const avatarApiMethods: IAvatarApiMethods = {
  mintSessionToken: async (botId) => {
    try {
      const {data} = await mintAvatarSessionTokenApi(botId);

      return data.sessionToken;
    } catch (error) {
      throw toAvatarApiError(error);
    }
  },
  startSession: async (dialogueId, botId) => {
    try {
      const {data} = await startAvatarSessionApi(dialogueId, botId);

      return data.id;
    } catch (error) {
      throw toAvatarApiError(error);
    }
  },
  reportSessionReady: async (videoCallId, providerSessionId) => {
    try {
      await reportAvatarSessionReadyApi(videoCallId, providerSessionId);
    } catch (error) {
      throw toAvatarApiError(error);
    }
  },
  endSession: async (videoCallId) => {
    let response: Response;

    try {
      response = await endAvatarSessionApi(videoCallId);
    } catch (error) {
      throw toAvatarApiError(error);
    }
    if (!response.ok) {
      const body: Partial<IAvatarSessionErrorResponse> = await response.json().catch(() => ({}));
      const apiError: IAvatarApiError = {status: response.status, message: body.error || response.statusText};

      throw apiError;
    }
  }
};
